import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, parse, startOfWeek, endOfWeek, eachDayOfInterval, setWeek, getWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { TrendingUp, TrendingDown, Minus, ArrowLeftRight, ChevronLeft, ChevronRight, Calendar, Coins } from 'lucide-react';
import { supabase } from '../lib/supabase';


interface PeriodData {
  total: number;
  cash: number;
  card: number;
  invoice: number;
  tips: number;
  daysWorked: number;
  avgPerDay: number;
}

interface WeekDayData {
  date: string;
  dayName: string;
  dayShort: string;
  cash: number;
  card: number;
  invoice: number;
  tips: number;
  total: number;
  isWeekend: boolean;
}

interface WeekYearData {
  weekStart: Date;
  weekEnd: Date;
  perDay: WeekDayData[];
  weekTotal: number;
  weekendTotal: number;
  weekdayTotal: number;
  cash: number;
  card: number;
  invoice: number;
  tips: number;
}

type CompareMode = 'months' | 'quarters' | 'weeks';

const QUARTERS = [
  { label: 'K1', months: [1, 2, 3] },
  { label: 'K2', months: [4, 5, 6] },
  { label: 'K3', months: [7, 8, 9] },
  { label: 'K4', months: [10, 11, 12] },
];

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

const WEEKDAY_NAMES = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];

function DiffBadge({ diff, pct }: { diff: number; pct: number }) {
  if (Math.abs(diff) < 0.01) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-400">
        <Minus className="h-3 w-3" /> €0.00
      </span>
    );
  }
  const positive = diff > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? '+' : ''}€{diff.toFixed(2)} ({positive ? '+' : ''}{pct.toFixed(1)}%)
    </span>
  );
}

export default function ComparisonView() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [mode, setMode] = useState<CompareMode>('months');
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTips, setShowTips] = useState(true);

  const [leftYear, setLeftYear] = useState(currentYear);
  const [leftMonth, setLeftMonth] = useState(currentMonth > 1 ? currentMonth - 1 : 12);
  const [leftQuarter, setLeftQuarter] = useState(1);

  const [rightYear, setRightYear] = useState(currentYear);
  const [rightMonth, setRightMonth] = useState(currentMonth);
  const [rightQuarter, setRightQuarter] = useState(Math.min(2, 4));

  const [leftData, setLeftData] = useState<PeriodData | null>(null);
  const [rightData, setRightData] = useState<PeriodData | null>(null);
  const [compared, setCompared] = useState(false);

  // Week comparison state
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const adjustedDate = now.getHours() < 7
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
      : now;
    return getWeek(adjustedDate, { weekStartsOn: 1, firstWeekContainsDate: 4 });
  });
  const [weekComparisonData, setWeekComparisonData] = useState<Record<number, WeekYearData>>({});
  const [weekLoading, setWeekLoading] = useState(false);
  const [weekTipsVisible, setWeekTipsVisible] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: settings } = await supabase
        .from('settings')
        .select('active_years, show_tips')
        .eq('user_id', user.id)
        .maybeSingle();
      if (settings?.active_years) {
        setActiveYears(settings.active_years.sort((a: number, b: number) => a - b));
      } else {
        setActiveYears([currentYear]);
      }
      setShowTips(settings?.show_tips ?? true);
      setWeekTipsVisible(settings?.show_tips ?? true);
    })();
  }, []);

  // Auto-fetch week comparison data when week, years, or tips visibility change
  useEffect(() => {
    if (mode !== 'weeks' || activeYears.length === 0) return;
    fetchWeekComparison(selectedWeek);
  }, [selectedWeek, activeYears, mode, weekTipsVisible]);

  const fetchWeekComparison = async (weekNum: number) => {
    setWeekLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setWeekLoading(false);
      return;
    }

    const sortedYears = [...activeYears].sort((a, b) => a - b);
    const result: Record<number, WeekYearData> = {};

    await Promise.all(sortedYears.map(async (year) => {
      const weekDate = setWeek(new Date(year, 5, 15), weekNum, { weekStartsOn: 1, firstWeekContainsDate: 4 });
      const weekStart = startOfWeek(weekDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(weekDate, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

      const dateStart = format(weekStart, 'yyyy-MM-dd');
      const dateEnd = format(weekEnd, 'yyyy-MM-dd');

      const [entriesResult, tipsResult] = await Promise.all([
        supabase.from('revenue_entries')
          .select('date, cash_revenue, card_revenue, invoice_revenue')
          .eq('user_id', user.id)
          .gte('date', dateStart)
          .lte('date', dateEnd),
        weekTipsVisible
          ? supabase.from('hidden_revenue')
              .select('date, amount')
              .eq('user_id', user.id)
              .gte('date', dateStart)
              .lte('date', dateEnd)
          : Promise.resolve({ data: null as null }),
      ]);

      const entriesMap: Record<string, { cash: number; card: number; invoice: number }> = {};
      (entriesResult.data || []).forEach(e => {
        entriesMap[e.date] = { cash: e.cash_revenue, card: e.card_revenue, invoice: e.invoice_revenue };
      });

      const tipsMap: Record<string, number> = {};
      if (tipsResult.data) {
        tipsResult.data.forEach(t => {
          if (!tipsMap[t.date]) tipsMap[t.date] = 0;
          tipsMap[t.date] += t.amount;
        });
      }

      const perDay: WeekDayData[] = days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const entry = entriesMap[dateStr];
        const tips = weekTipsVisible ? (tipsMap[dateStr] || 0) : 0;
        const cash = entry?.cash || 0;
        const card = entry?.card || 0;
        const invoice = entry?.invoice || 0;
        const total = cash + card + invoice + tips;
        return {
          date: dateStr,
          dayName: format(day, 'EEEE', { locale: nl }),
          dayShort: format(day, 'dd/MM'),
          cash, card, invoice, tips, total,
          isWeekend: day.getDay() === 5 || day.getDay() === 6,
        };
      });

      const weekTotal = perDay.reduce((s, d) => s + d.total, 0);
      const weekendTotal = perDay.filter(d => d.isWeekend).reduce((s, d) => s + d.total, 0);
      const weekdayTotal = perDay.filter(d => !d.isWeekend).reduce((s, d) => s + d.total, 0);
      const cash = perDay.reduce((s, d) => s + d.cash, 0);
      const card = perDay.reduce((s, d) => s + d.card, 0);
      const invoice = perDay.reduce((s, d) => s + d.invoice, 0);
      const tips = perDay.reduce((s, d) => s + d.tips, 0);

      result[year] = { weekStart, weekEnd, perDay, weekTotal, weekendTotal, weekdayTotal, cash, card, invoice, tips };
    }));

    setWeekComparisonData(result);
    setWeekLoading(false);
  };

  const fetchPeriodData = async (year: number, month: number | null, quarter: number | null): Promise<PeriodData> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { total: 0, cash: 0, card: 0, invoice: 0, tips: 0, daysWorked: 0, avgPerDay: 0 };

    let dateStart: string;
    let dateEnd: string;

    if (month !== null) {
      const d = parse(`${year}-${String(month).padStart(2, '0')}`, 'yyyy-MM', new Date());
      dateStart = format(startOfMonth(d), 'yyyy-MM-dd');
      dateEnd = format(endOfMonth(d), 'yyyy-MM-dd');
    } else {
      const q = QUARTERS[(quarter ?? 1) - 1];
      const firstMonth = parse(`${year}-${String(q.months[0]).padStart(2, '0')}`, 'yyyy-MM', new Date());
      const lastMonth = parse(`${year}-${String(q.months[2]).padStart(2, '0')}`, 'yyyy-MM', new Date());
      dateStart = format(startOfMonth(firstMonth), 'yyyy-MM-dd');
      dateEnd = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
    }

    const [{ data: entries }, { data: tipsData }] = await Promise.all([
      supabase.from('revenue_entries').select('cash_revenue, card_revenue, invoice_revenue').eq('user_id', user.id).gte('date', dateStart).lte('date', dateEnd),
      supabase.from('hidden_revenue').select('amount').eq('user_id', user.id).gte('date', dateStart).lte('date', dateEnd),
    ]);

    const cash = (entries || []).reduce((s, e) => s + e.cash_revenue, 0);
    const card = (entries || []).reduce((s, e) => s + e.card_revenue, 0);
    const invoice = (entries || []).reduce((s, e) => s + e.invoice_revenue, 0);
    const tips = (tipsData || []).reduce((s, e) => s + e.amount, 0);
    const total = cash + card + invoice;
    const daysWorked = (entries || []).filter(e => e.cash_revenue + e.card_revenue + e.invoice_revenue > 0).length;

    return {
      total,
      cash,
      card,
      invoice,
      tips,
      daysWorked,
      avgPerDay: daysWorked > 0 ? total / daysWorked : 0,
    };
  };

  const handleCompare = async () => {
    setLoading(true);
    setCompared(false);
    const [l, r] = await Promise.all([
      fetchPeriodData(leftYear, mode === 'months' ? leftMonth : null, mode === 'quarters' ? leftQuarter : null),
      fetchPeriodData(rightYear, mode === 'months' ? rightMonth : null, mode === 'quarters' ? rightQuarter : null),
    ]);
    setLeftData(l);
    setRightData(r);
    setCompared(true);
    setLoading(false);
  };

  const getPeriodLabel = (year: number, month: number | null, quarter: number | null) => {
    if (month !== null) return `${MONTH_NAMES[month - 1]} ${year}`;
    return `K${quarter} ${year}`;
  };

  const diff = (l: number, r: number) => r - l;
  const pct = (l: number, r: number) => l === 0 ? 0 : ((r - l) / l) * 100;

  const years = activeYears.length > 0 ? activeYears : [currentYear];
  const sortedYears = [...years].sort((a, b) => b - a);

  const euro = (v: number) => `€${v.toFixed(2)}`;

  // Week selector helpers
  const maxWeek = 53;
  const changeWeek = (delta: number) => {
    setSelectedWeek(w => {
      const next = w + delta;
      if (next < 1) return 1;
      if (next > maxWeek) return maxWeek;
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        Vergelijken
      </h2>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
        {/* Mode toggle */}
        <div className="flex flex-wrap gap-2">
          {(['months', 'quarters', 'weeks'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setCompared(false); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === m
                  ? 'text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              style={mode === m ? { backgroundColor: 'var(--accent-color)' } : undefined}
            >
              {m === 'months' ? 'Maanden' : m === 'quarters' ? 'Kwartalen' : 'Weken'}
            </button>
          ))}
        </div>

        {/* === WEEKS MODE === */}
        {mode === 'weeks' && (
          <div className="space-y-4">
            {/* Week selector */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => changeWeek(-1)}
                  disabled={selectedWeek <= 1}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="text-center min-w-[120px]">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">Week {selectedWeek}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {sortedYears.length} {sortedYears.length === 1 ? 'jaar' : 'jaren'}
                  </p>
                </div>
                <button
                  onClick={() => changeWeek(1)}
                  disabled={selectedWeek >= maxWeek}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Week quick-select dropdown */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={selectedWeek}
                    onChange={(e) => setSelectedWeek(Number(e.target.value))}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 pr-8 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none cursor-pointer"
                  >
                    {Array.from({ length: maxWeek }, (_, i) => i + 1).map(w => (
                      <option key={w} value={w}>Week {w}</option>
                    ))}
                  </select>
                  <Calendar className="h-4 w-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                {showTips && (
                  <button
                    onClick={() => {
                      const newVal = !weekTipsVisible;
                      setWeekTipsVisible(newVal);
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      weekTipsVisible
                        ? 'bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Coins className="h-4 w-4" />
                    Fooi {weekTipsVisible ? 'verbergen' : 'tonen'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* === MONTHS / QUARTERS MODE === */}
        {mode !== 'weeks' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left period */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Periode A</p>
                <div className="flex gap-2">
                  <select
                    value={leftYear}
                    onChange={(e) => setLeftYear(Number(e.target.value))}
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  {mode === 'months' ? (
                    <select
                      value={leftMonth}
                      onChange={(e) => setLeftMonth(Number(e.target.value))}
                      className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
                    </select>
                  ) : (
                    <select
                      value={leftQuarter}
                      onChange={(e) => setLeftQuarter(Number(e.target.value))}
                      className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {[1, 2, 3, 4].map(q => <option key={q} value={q}>K{q}</option>)}
                    </select>
                  )}
                </div>
              </div>

              {/* Right period */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Periode B</p>
                <div className="flex gap-2">
                  <select
                    value={rightYear}
                    onChange={(e) => setRightYear(Number(e.target.value))}
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  {mode === 'months' ? (
                    <select
                      value={rightMonth}
                      onChange={(e) => setRightMonth(Number(e.target.value))}
                      className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
                    </select>
                  ) : (
                    <select
                      value={rightQuarter}
                      onChange={(e) => setRightQuarter(Number(e.target.value))}
                      className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {[1, 2, 3, 4].map(q => <option key={q} value={q}>K{q}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={handleCompare}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-colors"
              style={{ backgroundColor: 'var(--accent-color)' }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <ArrowLeftRight className="h-4 w-4" />
                  Vergelijken
                </>
              )}
            </button>
          </>
        )}
      </div>

      {/* === WEEKS RESULTS === */}
      {mode === 'weeks' && weekLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--accent-color)' }} />
        </div>
      )}

      {mode === 'weeks' && !weekLoading && Object.keys(weekComparisonData).length > 0 && (
        <div className="space-y-6">
          {/* Year summary cards */}
          <div className={`grid gap-4 ${sortedYears.length === 1 ? 'grid-cols-1' : sortedYears.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
            {sortedYears.map((year, idx) => {
              const data = weekComparisonData[year];
              if (!data) return null;
              const prevYear = sortedYears[idx + 1];
              const prevData = prevYear ? weekComparisonData[prevYear] : null;
              const hasComparison = !!prevData;

              return (
                <div
                  key={year}
                  className={`rounded-lg shadow overflow-hidden bg-white dark:bg-gray-800`}
                  style={year === currentYear ? { boxShadow: '0 0 0 2px var(--accent-color), 0 4px 6px -1px rgb(0 0 0 / 0.1)' } : undefined}
                >
                  {/* Year header */}
                  <div
                    className={`px-5 py-3 border-b border-gray-100 dark:border-gray-700 ${
                      year === currentYear ? '' : 'bg-gray-50 dark:bg-gray-700/50'
                    }`}
                    style={year === currentYear ? { backgroundColor: 'color-mix(in srgb, var(--accent-color) 12%, transparent)' } : undefined}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{year}</span>
                      {year === currentYear && (
                        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--accent-color)' }}>Huidige</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {format(data.weekStart, 'dd MMM', { locale: nl })} - {format(data.weekEnd, 'dd MMM yyyy', { locale: nl })}
                    </p>
                  </div>

                  {/* Week total */}
                  <div className="px-5 py-4 space-y-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Weekomzet</p>
                      <div className="flex items-baseline justify-between mt-1">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{euro(data.weekTotal)}</p>
                        {hasComparison && (
                          <DiffBadge diff={diff(prevData!.weekTotal, data.weekTotal)} pct={pct(prevData!.weekTotal, data.weekTotal)} />
                        )}
                      </div>
                    </div>

                    {/* Weekend total - highlighted */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Weekend
                      </p>
                      <div className="flex items-baseline justify-between mt-1">
                        <p className="text-xl font-bold text-amber-900 dark:text-amber-300">{euro(data.weekendTotal)}</p>
                        {hasComparison && (
                          <DiffBadge diff={diff(prevData!.weekendTotal, data.weekendTotal)} pct={pct(prevData!.weekendTotal, data.weekendTotal)} />
                        )}
                      </div>
                      <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                        {data.weekTotal > 0 ? `${((data.weekendTotal / data.weekTotal) * 100).toFixed(0)}% van week` : '—'}
                      </p>
                    </div>

                    {/* Weekday total */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Doordeweeks</p>
                      <div className="flex items-baseline justify-between mt-1">
                        <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">{euro(data.weekdayTotal)}</p>
                        {hasComparison && (
                          <DiffBadge diff={diff(prevData!.weekdayTotal, data.weekdayTotal)} pct={pct(prevData!.weekdayTotal, data.weekdayTotal)} />
                        )}
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div>
                        <p className="text-xs text-gray-400">Contant</p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{euro(data.cash)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Pin</p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{euro(data.card)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Rekening</p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{euro(data.invoice)}</p>
                      </div>
                      {weekTipsVisible && (
                        <div>
                          <p className="text-xs text-gray-400">Fooi</p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{euro(data.tips)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Per-day comparison table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Per dag vergelijking</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700/50">
                      Dag
                    </th>
                    {sortedYears.map(y => (
                      <th key={y} className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">
                        {y}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {WEEKDAY_NAMES.map((dayName, dayIdx) => {
                    const isWeekendRow = dayIdx === 4 || dayIdx === 5;
                    // Get the day data for the most recent year to use as reference for date display
                    const refYear = sortedYears[0];
                    const refData = weekComparisonData[refYear];
                    const refDay = refData?.perDay[dayIdx];

                    return (
                      <tr
                        key={dayName}
                        className={isWeekendRow ? 'bg-amber-50/60 dark:bg-amber-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}
                      >
                        <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-inherit">
                          <div className="flex items-center gap-2">
                            {isWeekendRow && <Calendar className="h-3.5 w-3.5 text-amber-500" />}
                            <div>
                              <p className={`text-sm font-medium ${isWeekendRow ? 'text-amber-700 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                {dayName}
                              </p>
                              {refDay && (
                                <p className="text-xs text-gray-400">{refDay.dayShort}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        {sortedYears.map(year => {
                          const dayData = weekComparisonData[year]?.perDay[dayIdx];
                          const value = dayData?.total ?? 0;
                          const prevYear = sortedYears[sortedYears.indexOf(year) + 1];
                          const prevDayData = prevYear ? weekComparisonData[prevYear]?.perDay[dayIdx] : null;
                          const prevValue = prevDayData?.total ?? 0;

                          return (
                            <td key={year} className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="flex flex-col items-end gap-0.5">
                                <span className={`text-sm font-semibold ${
                                  value > 0
                                    ? isWeekendRow ? 'text-amber-700 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'
                                    : 'text-gray-300 dark:text-gray-600'
                                }`}>
                                  {euro(value)}
                                </span>
                                {prevYear && value > 0 && prevValue > 0 && value !== prevValue && (
                                  <span className={`text-[10px] ${value > prevValue ? 'text-emerald-500' : 'text-red-400'}`}>
                                    {value > prevValue ? '+' : ''}{((value - prevValue) / prevValue * 100).toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                {/* Weekend subtotal row */}
                <tbody className="border-t-2 border-amber-200 dark:border-amber-800">
                  <tr className="bg-amber-100/70 dark:bg-amber-900/20">
                    <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-amber-100/70 dark:bg-amber-900/20">
                      <span className="text-sm font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide">Weekend</span>
                    </td>
                    {sortedYears.map(year => {
                      const data = weekComparisonData[year];
                      const value = data?.weekendTotal ?? 0;
                      const prevYear = sortedYears[sortedYears.indexOf(year) + 1];
                      const prevValue = prevYear ? weekComparisonData[prevYear]?.weekendTotal ?? 0 : 0;

                      return (
                        <td key={year} className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-base font-bold text-amber-800 dark:text-amber-300">{euro(value)}</span>
                            {prevYear && value > 0 && prevValue > 0 && value !== prevValue && (
                              <span className={`text-[10px] ${value > prevValue ? 'text-emerald-500' : 'text-red-400'}`}>
                                {value > prevValue ? '+' : ''}{((value - prevValue) / prevValue * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
                {/* Week total row */}
                <tbody className="border-t border-gray-200 dark:border-gray-700">
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-gray-50 dark:bg-gray-700/50">
                      <span className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Totaal</span>
                    </td>
                    {sortedYears.map(year => {
                      const data = weekComparisonData[year];
                      const value = data?.weekTotal ?? 0;
                      const prevYear = sortedYears[sortedYears.indexOf(year) + 1];
                      const prevValue = prevYear ? weekComparisonData[prevYear]?.weekTotal ?? 0 : 0;

                      return (
                        <td key={year} className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-base font-bold" style={{ color: 'var(--accent-color)' }}>{euro(value)}</span>
                            {prevYear && value > 0 && prevValue > 0 && value !== prevValue && (
                              <span className={`text-[10px] ${value > prevValue ? 'text-emerald-500' : 'text-red-400'}`}>
                                {value > prevValue ? '+' : ''}{((value - prevValue) / prevValue * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === MONTHS / QUARTERS RESULTS === */}
      {mode !== 'weeks' && compared && leftData && rightData && (
        <div className="space-y-4">
          {/* Header labels */}
          <div className="grid grid-cols-3 gap-4 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-1">
            <div>{getPeriodLabel(leftYear, mode === 'months' ? leftMonth : null, mode === 'quarters' ? leftQuarter : null)}</div>
            <div className="text-center">Verschil</div>
            <div className="text-right">{getPeriodLabel(rightYear, mode === 'months' ? rightMonth : null, mode === 'quarters' ? rightQuarter : null)}</div>
          </div>

          {/* Total card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Totale Omzet</span>
            </div>
            <div className="grid grid-cols-3 gap-4 items-center px-4 py-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">€{leftData.total.toFixed(2)}</div>
              <div className="text-center">
                <DiffBadge diff={diff(leftData.total, rightData.total)} pct={pct(leftData.total, rightData.total)} />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white text-right">€{rightData.total.toFixed(2)}</div>
            </div>
          </div>

          {/* Breakdown rows */}
          {[
            { label: 'Contant', lVal: leftData.cash, rVal: rightData.cash },
            { label: 'Pin', lVal: leftData.card, rVal: rightData.card },
            { label: 'Rekening', lVal: leftData.invoice, rVal: rightData.invoice },
            { label: 'Fooi', lVal: leftData.tips, rVal: rightData.tips },
            { label: 'Werkdagen', lVal: leftData.daysWorked, rVal: rightData.daysWorked, isCount: true },
            { label: 'Gem. per dag', lVal: leftData.avgPerDay, rVal: rightData.avgPerDay },
          ].map(({ label, lVal, rVal, isCount }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 items-center px-4 py-3">
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {isCount ? lVal : `€${lVal.toFixed(2)}`}
                </div>
                <div className="text-center">
                  {isCount ? (
                    <span className={`text-xs font-medium ${rVal - lVal > 0 ? 'text-emerald-400' : rVal - lVal < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      {rVal - lVal > 0 ? '+' : ''}{rVal - lVal}
                    </span>
                  ) : (
                    <DiffBadge diff={diff(lVal, rVal)} pct={pct(lVal, rVal)} />
                  )}
                </div>
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100 text-right">
                  {isCount ? rVal : `€${rVal.toFixed(2)}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
